import FingerprintJS from '@fingerprintjs/fingerprintjs'
import {AfterContentInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {HttpErrorResponse} from "@angular/common/http";
import {BaseResponseMessage, ErrorResponseMessage} from 'shared/commands';
import {ConnectCommandNamespace} from 'shared/commands/connect';
import {Errors, getMessageIdByError} from 'shared/errors';
import {AnalyticsService, AuthFailureReasons, Pages} from '../../services/analytics.service';
import {environment} from 'src/environments/environment';
import {MajorityDialogComponentComponent} from "./majority-dialog-component/majority-dialog-component.component";
import {removeNonVisibleSymbols} from "shared/utils";
import {LoginValidator, PasswordValidator} from "shared/validators";
import {HttpService} from "../../services/http.service";
import {ChatCustomValidators} from "../../utils/angularcustomvalidators";
import {formatTime, getPasswordErrorTextId, isInBrowser} from "../../utils/clientutils";
import {DisconnectResponseMessage, RemoteCommandService} from "../../services/remote.command.service";
import {TranslateService} from "@ngx-translate/core";
import {LANGUAGE_RU} from "../../../../shared/language";
import {ChatStateService} from "../../services/chat.state.service";
import PasswordRequiredError = ConnectCommandNamespace.PasswordRequiredError;
import AuthBlockedResponse = ConnectCommandNamespace.AuthBlockedResponse;

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent implements OnInit, AfterContentInit, OnDestroy {
    private static KEY_USERNAME = 'username';
    private static KEY_SEX = 'sex';

    readonly STEP_LOGIN = AuthStep.login
    readonly STEP_ANONYMOUS = AuthStep.anonymousUser
    readonly STEP_REGISTERED = AuthStep.registeredUser

    readonly LOGIN_MAX_LENGTH = LoginValidator.MAX_LENGTH
    readonly PASSWORD_MAX_LENGTH = PasswordValidator.MAX_LENGTH

    loginControl: FormControl;
    passwordControl: FormControl;
    genderControl: FormControl;

    recapthcaError: boolean;
    progress = true;
    needEmailConfirm = false;

    invisibleFieldShowed = false;

    isInvisibleChecked: boolean = false;
    error: string;

    captchaToken: string = ""
    @ViewChild('captcha', {static: false}) captchaRef: ElementRef;

    hidePassword = true

    authStep: AuthStep

    captchaSize: string = "normal"

    private visitorId = '';
    private timer: number;

    private readonly errorCallback
    private readonly disconnectCallback
    private readonly messageCallback

    private audio: HTMLAudioElement;

    constructor(private httpService: HttpService,
                private remoteCommandService: RemoteCommandService,
                private analytics: AnalyticsService,
                private chatStateService: ChatStateService,
                private router: Router,
                private snackBar: MatSnackBar,
                private dialog: MatDialog,
                private translate: TranslateService) {

        if (isInBrowser()) {
            this.audio = new Audio('assets/message.mp3');
        }

        this.authStep = AuthStep.login
        this.recapthcaError = false;
        this.progress = true;
        this.analytics.pageChanged(Pages.login);

        this.loginControl = new FormControl('', [Validators.required, ChatCustomValidators.loginValidator(this.translate.currentLang() == LANGUAGE_RU)]);
        this.passwordControl = new FormControl('', [Validators.required, ChatCustomValidators.passwordValidator()]);
        this.genderControl = new FormControl({value: '', disabled: false}, [Validators.required]);

        const self = this;

        if (isInBrowser()) {
            setTimeout(function () {
                // Initialize an agent at application startup.
                const fpPromise = FingerprintJS.load();

                (async () => {
                    // Get the visitor identifier when you need it.
                    const fp = await fpPromise
                    const result = await fp.get()

                    clearInterval(self.timer);

                    self.visitorId = result.visitorId

                    if (!environment.production) {
                        console.log(self.visitorId);
                    }

                    self.progress = false;
                })()
            }, 10_00);

            this.timer = window.setTimeout(() => {
                analytics.fingerprintError();
                self.progress = false;
            }, 10_000);
        } else {
            this.progress = false;
        }

        this.errorCallback = this.onConnectError.bind(this)
        this.disconnectCallback = this.onDisconnect.bind(this)
        this.messageCallback = this.onConnectCommandResponse.bind(this)
    }

    ngOnInit() {
        this.chatStateService.clearData()

        if (isInBrowser()) {
            const login = window.sessionStorage.getItem(LoginComponent.KEY_USERNAME);
            const sex = window.sessionStorage.getItem(LoginComponent.KEY_SEX);

            if (window.innerWidth < 425) {
                this.captchaSize = "compact"
            }

            if (login) {
                this.loginControl.setValue(login);
            }

            if (sex) {
                this.genderControl.setValue(sex);
            }
        }

        this.remoteCommandService.disconnect();

        this.remoteCommandService.subscribeOnCommand(RemoteCommandService.SERVICE_COMMAND_SOCKET_ERROR, this.errorCallback);
        this.remoteCommandService.subscribeOnCommand(RemoteCommandService.SERVICE_COMMAND_SOCKET_DISCONNECT, this.disconnectCallback);
        this.remoteCommandService.subscribeOnCommand(ConnectCommandNamespace.COMMAND_CONNECT, this.messageCallback)

        if (!environment.production) {
            //Чтоб на локальном сервере не приходилось вводить капчу
            this.captchaToken = "12345";
        }
    }

    ngAfterContentInit(): void {
        //bu({required: {e: -4, f: -3, o: -3, s: -1, c: -3}, insecure: true, unsupported: true, api: 2023.04})
    }

    ngOnDestroy(): void {
        this.remoteCommandService.unsubscribeCallback(this.errorCallback);
        this.remoteCommandService.unsubscribeCallback(this.disconnectCallback);
        this.remoteCommandService.unsubscribeCallback(this.messageCallback)
    }

    captchaResolved(token: string) {
        this.captchaToken = token
    }

    captchaExpired() {
        this.captchaToken = ""
    }

    captchaError(error: any) {
        this.captchaToken = ""
    }


    getLoginError(): string {
        if (this.loginControl.hasError('required')) {
            return this.translate.instant('enter_login')
        } else if (this.loginControl.hasError('customValidatorError')) {
            return this.translate.instant(getMessageIdByError(this.loginControl.getError('customValidatorError').error))
        }
        return ""
    }

    getPasswordError(formControl: FormControl): string {
        return this.translate.instant(getPasswordErrorTextId(formControl))
    }

    cancel() {
        this.loginControl.setValue("")
        this.genderControl.setValue(undefined)
        this.passwordControl.setValue("")

        this.loginControl.enable()
        this.genderControl.enable()
        this.passwordControl.enable();

        this.authStep = AuthStep.login
        this.invisibleFieldShowed = false
        this.isInvisibleChecked = false
        this.error = null
        this.needEmailConfirm = false
    }

    login() {
        if (this.authStep == AuthStep.login && this.loginControl.valid) {
            const login = removeNonVisibleSymbols(this.loginControl.value.trim())

            this.checkUser(login)
        } else if (!this.loginControl.invalid && !this.genderControl.invalid && this.captchaToken) {
            if (this.authStep == AuthStep.anonymousUser) { //Показываем соглашение только для анонимных пользователей
                const dialogRef = this.dialog.open(MajorityDialogComponentComponent);

                dialogRef.afterClosed().subscribe(result => {
                    if (!result) {
                        this.snackBar.open(this.translate.instant('only_for_18_years_old'), this.translate.instant('understand'), {duration: 5_000});
                        return;
                    }

                    this.connectToServer(this.captchaToken)
                    this.recapthcaError = false;
                });
            } else if (this.authStep == AuthStep.registeredUser && !this.passwordControl.valid) {
                return
            } else {
                this.connectToServer(this.captchaToken)
                this.recapthcaError = false;
            }
        }
    }

    resendConfirmationEmail() {
        if (!this.loginControl.valid) {
            return
        }

        this.progress = true
        this.error = null
        this.needEmailConfirm = false
        this.httpService.resendConfirmationEmail(this.loginControl.value).subscribe({
            next: (response => {
                if (response['success']) {
                    this.error = this.translate.instant('confirmation_sent_successfully')
                } else {
                    this.checkRequestErrors(response)

                    if (this.captchaRef) {
                        (<any>this.captchaRef).reset()
                    }
                }

                this.progress = false
            }), error: (err => {
                this.checkResponseException(err)
                if (this.captchaRef) {
                    (<any>this.captchaRef).reset()
                }
                this.progress = false
            })
        })
    }

    private onConnectError() {
        if (!environment.production) {
            console.log("onError")
        }

        this.progress = false;
        this.error = this.translate.instant(getMessageIdByError(Errors.CANT_CONNECT))
        this.analytics.authFailure(AuthFailureReasons.server);
        (<any>this.captchaRef).reset();
    }

    private onDisconnect(responseMessage: BaseResponseMessage) {
        const statusCode = (responseMessage as DisconnectResponseMessage).statusCode
        if (!environment.production) {
            console.log("onDisconnect", statusCode)
        }

        if (statusCode === 1011) {
            this.progress = false;
            this.error = this.translate.instant(getMessageIdByError(Errors.SERVER_ERROR))
            (<any>this.captchaRef).reset();
        }
        this.progress = false;
    }

    private onConnectCommandResponse(data: BaseResponseMessage) {
        if (data.success) {
            this.router.navigate(['chat']).then();
        } else {
            this.checkCommandResponseError(data as ErrorResponseMessage)
        }
    }

    private checkCommandResponseError(data: ErrorResponseMessage) {
        if (data.error === Errors.PASSWORD_REQUIRED) {
            const error = data as PasswordRequiredError
            //Вообще по идее сюда не должно попадать
            this.authStep = AuthStep.registeredUser
            this.invisibleFieldShowed = error.showInvisibleButton
            this.loginControl.setValue(error.login)
            this.genderControl.setValue(error.sex)
            this.loginControl.disable()
            this.genderControl.disable();
            return;
        } else if (data.error === Errors.WRONG_PASSWORD) {
            this.passwordControl.setErrors({'wrongPassword': true});
        } else if (data.error === Errors.USER_IS_BLOCKED) {
            const error = data as ConnectCommandNamespace.UserBlockedResponse
            this.showMessageAboutBlocking(error.message, error.willBeUnblockThrough)
        } else if (data.error === Errors.NEED_UPDATE_CLIENT) {
            this.showNeedUpdateMessage()
        } else if (data.error === Errors.ENTERS_ATTEMPTS_EXCEEDED) {
            const error = (data as AuthBlockedResponse)
            this.showEnterAttemptExceedError(error.willBeUnblockThrough)
        } else if (data.error === Errors.EMAIL_NOT_CONFIRMED) {
            this.error = this.translate.instant(getMessageIdByError(data.error))
            this.needEmailConfirm = true
        } else {
            this.error = this.translate.instant(getMessageIdByError(data.error))
        }

        if (this.captchaRef) {
            (<any>this.captchaRef).reset();
        }
    }

    private checkRequestErrors(response: object) {
        //TODO: А не привести ли нам к одному типу (ErrorResponseMessage) ошибки которые приходят по секту и по http
        //чтоб можно было их обрабатывать одним и тем же методом
        let error = response['error']

        if (error == Errors.EMPTY_LOGIN) {
            this.loginControl.setErrors(ChatCustomValidators.error(error))
        } else if (error == Errors.LOGIN_TOO_LONG) {
            this.passwordControl.setErrors(ChatCustomValidators.error(error))
        } else if (error == Errors.ENTERS_ATTEMPTS_EXCEEDED) {
            this.showEnterAttemptExceedError(response['willBeUnblockThrough'])
        } else if (error == Errors.NEED_UPDATE_CLIENT) {
            this.showNeedUpdateMessage()
        } else if (error == Errors.USER_IS_BLOCKED) {
            this.showMessageAboutBlocking(response['reason'], response['willBeUnblockThrough'])
        } else {
            this.error = this.translate.instant(getMessageIdByError(error))
        }
    }

    private showMessageAboutBlocking(message: string, timeToUnblock: number) {
        if (!message) {
            message = this.translate.instant('you_are_blocked')
        }

        let messageSecondPart = ''
        if (timeToUnblock != ConnectCommandNamespace.UserBlockedResponse.PERMANENT_UNBLOCK_TIME || timeToUnblock > 0) {
            messageSecondPart = this.translate.instant('you_need_to_wait', {'time': formatTime(this.translate, timeToUnblock)})
        }

        this.error = `${message}. ${messageSecondPart}`
    }

    private showNeedUpdateMessage() {
        const ref = this.snackBar.open(this.translate.instant('update_available'), this.translate.instant('reload'));
        ref.onAction().subscribe(() => {
            window.location.reload();
        });
    }

    private showEnterAttemptExceedError(willBeUnblockThrough: number) {
        this.error = this.translate.instant('too_much_attempts_for_entrance', {time: formatTime(this.translate, willBeUnblockThrough, true)})
    }

    private checkResponseException(error: any) {
        let code: number = -2

        if (error instanceof HttpErrorResponse) {
            code = error.status
        }

        this.error = this.translate.instant(getMessageIdByError(code));
    }

    private connectToServer(token: string) {
        //хак для того чтоб можно было воспроизводить звуки
        this.audio.volume = 0.01
        this.audio.play()
            .catch((error) => {
                if (!environment.production) {
                    console.log('sound not played', error)
                }
            })
            .finally(() => {
                if (!environment.production) {
                    console.log('sound played')
                }
            })

        const login = removeNonVisibleSymbols(this.loginControl.value.trim())

        const sex = this.genderControl.value.trim();
        const password = this.passwordControl ? this.passwordControl.value.trim() : '';

        if (!environment.production) {
            console.log(token);
        }

        this.progress = true;
        this.passwordControl.setErrors(null);
        this.error = null;
        this.needEmailConfirm = false

        this.chatStateService.clearData()
        this.remoteCommandService.connect(login, sex, password, this.isInvisibleChecked, this.visitorId, token);

        window.sessionStorage.setItem(LoginComponent.KEY_USERNAME, login);
        window.sessionStorage.setItem(LoginComponent.KEY_SEX, sex);
    }

    private checkUser(login: string) {
        this.progress = true
        this.error = null
        this.needEmailConfirm = false
        this.httpService.setup(login, environment.version, this.visitorId).subscribe({
            next: (response => {
                if (response['success']) {
                    this.loginControl.disable();

                    if (!response['registered']) {
                        this.loginControl.setValue(response['login'])
                        this.invisibleFieldShowed = response['invisible']
                        this.authStep = AuthStep.anonymousUser
                    } else {
                        this.loginControl.setValue(response['login'])
                        this.genderControl.setValue(response['sex'])
                        this.invisibleFieldShowed = response['invisible']
                        this.authStep = AuthStep.registeredUser
                        this.genderControl.disable()
                    }
                } else {
                    this.checkRequestErrors(response)

                    if (this.captchaRef) {
                        (<any>this.captchaRef).reset()
                    }
                }

                this.progress = false
            }),
            error: (err => {
                this.checkResponseException(err)

                if (this.captchaRef) {
                    (<any>this.captchaRef).reset()
                }
                this.progress = false
            })
        })
    }
}

enum AuthStep {
    login,
    anonymousUser,
    registeredUser,
}
